/**
 * ToastItem — individual toast notification with auto-dismiss and animations.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NotificationRecord } from '@/types/notifications';

const AUTO_DISMISS_MS = 8000;

interface ToastItemProps {
  notification: NotificationRecord;
  onDismiss: (id: string) => void;
  onClick: (notification: NotificationRecord) => void;
}

export function ToastItem({ notification, onDismiss, onClick }: ToastItemProps) {
  const [isExiting, setIsExiting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remainingRef = useRef(AUTO_DISMISS_MS);
  const startTimeRef = useRef(Date.now());
  const navigate = useNavigate();

  const isUrgent = notification.priority === 'urgent';
  const borderColor = isUrgent ? 'border-l-rose-500' : 'border-l-amber-500';

  const dismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => onDismiss(notification.notification_id), 200);
  }, [onDismiss, notification.notification_id]);

  // Auto-dismiss for high priority
  useEffect(() => {
    if (isUrgent || isPaused) return;

    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(dismiss, remainingRef.current);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isUrgent, isPaused, dismiss]);

  const handlePause = () => {
    if (isUrgent) return;
    setIsPaused(true);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      remainingRef.current -= Date.now() - startTimeRef.current;
    }
  };

  const handleResume = () => {
    if (isUrgent) return;
    setIsPaused(false);
  };

  const handleClick = () => {
    onClick(notification);
    if (notification.metadata?.link_target) {
      navigate(notification.metadata.link_target);
    }
  };

  const title = notification.title.length > 60
    ? notification.title.slice(0, 60) + '…'
    : notification.title;

  const body = notification.body.length > 100
    ? notification.body.slice(0, 100) + '…'
    : notification.body;

  return (
    <div
      className={`pointer-events-auto w-80 rounded-lg border-l-4 bg-dark-800 shadow-xl ${borderColor} ${
        isExiting ? 'animate-fade-out' : 'animate-slide-in-right'
      }`}
      onMouseEnter={handlePause}
      onMouseLeave={handleResume}
      onFocus={handlePause}
      onBlur={handleResume}
      role={isUrgent ? 'alert' : 'status'}
      aria-live={isUrgent ? 'assertive' : 'polite'}
    >
      <div className="flex items-start gap-3 p-3">
        <button
          onClick={handleClick}
          className="flex-1 min-w-0 text-left"
          aria-label={`Notification: ${notification.title}`}
        >
          <p className="text-sm font-medium text-dark-100 truncate">{title}</p>
          <p className="text-xs text-dark-400 mt-1 line-clamp-2">{body}</p>
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            dismiss();
          }}
          className="flex-shrink-0 rounded p-1 text-dark-400 hover:text-dark-200 hover:bg-dark-700 transition-colors"
          aria-label="Dismiss notification"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
